import type { Character } from '../domain/character.ts';
import { CHARACTER_SCHEMA_VERSION } from '../domain/character.ts';
import { assertCharacter, validateCharacter } from '../domain/validateCharacter.ts';
import { migrateCharacterRecord } from '../domain/migrateCharacter.ts';
import type { StoragePort } from './storagePort.ts';
import type { LibraryIndex } from './libraryIndex.ts';
import { emptyIndex, entryFor, isLibraryIndex, removeEntry, upsertEntry } from './libraryIndex.ts';
import { WriteQueue } from './writeQueue.ts';

/**
 * Every character shares one index.json, so index updates need their own lock.
 * Character files are keyed per character; the index is keyed once, globally.
 */
const INDEX_QUEUE_KEY = 'library-index';

/**
 * All reads and writes of saved characters go through here.
 * The index is a convenience cache; if it is missing or damaged it is rebuilt
 * from the character files, which are the source of truth.
 */
export class CharacterRepository {
  private readonly storage: StoragePort;
  private readonly now: () => string;
  private readonly queue: WriteQueue;

  constructor(
    storage: StoragePort,
    now: () => string = () => new Date().toISOString(),
    queue: WriteQueue = new WriteQueue(),
  ) {
    this.storage = storage;
    this.now = now;
    this.queue = queue;
  }

  /** Resolves once every queued write has finished. */
  drain(): Promise<void> {
    return this.queue.drain();
  }

  /**
   * Every stored crawler file ID, from the storage authority rather than the
   * convenience index. Safety scans use this so a damaged crawler cannot vanish
   * merely because rebuilding the index skipped a record that failed validation.
   */
  listStoredIds(): Promise<string[]> {
    return this.storage.listCharacterIds();
  }

  async loadIndex(): Promise<LibraryIndex> {
    const raw = await this.storage.readIndex();
    if (isLibraryIndex(raw)) return raw;
    return this.rebuildIndex();
  }

  async rebuildIndex(): Promise<LibraryIndex> {
    const ids = await this.storage.listCharacterIds();
    let index = emptyIndex();
    for (const id of ids) {
      const raw = migrateCharacterRecord(await this.storage.readCharacter(id));
      if (validateCharacter(raw).valid) {
        index = upsertEntry(index, entryFor(raw as Character));
      }
    }
    await this.storage.writeIndex(index);
    return index;
  }

  async load(id: string): Promise<Character> {
    const raw = await this.storage.readCharacter(id);
    if (raw === null) throw new Error(`No saved character with id ${id}`);
    return assertCharacter(migrateCharacterRecord(raw));
  }

  /**
   * The record exactly as `save` would write it - schema version, timestamp,
   * validated - WITHOUT writing it. For a caller that writes it as part of a
   * larger atomic transaction (`CheckpointCommitter` on a store with
   * `writeTogether`), so both paths put the same bytes on disk.
   */
  stamp(character: Character): Character {
    const stamped: Character = {
      ...character,
      schemaVersion: CHARACTER_SCHEMA_VERSION,
      updatedAt: this.now(),
    };
    assertCharacter(stamped);
    return stamped;
  }

  /**
   * Runs `write` holding the same two locks `save` holds - this crawler's and
   * the shared index's - and hands it the index as it must read once `stamped`
   * has landed. `write` is responsible for persisting BOTH; nothing is written
   * here. If `write` rejects, nothing was changed by this method.
   */
  writeWithIndex<T>(stamped: Character, write: (nextIndex: LibraryIndex) => Promise<T>): Promise<T> {
    return this.queue.run(`character:${stamped.id}`, () =>
      this.queue.run(INDEX_QUEUE_KEY, async () => {
        const index = await this.loadIndex();
        return write(upsertEntry(index, entryFor(stamped)));
      }),
    );
  }

  async save(character: Character): Promise<Character> {
    const stamped = this.stamp(character);
    // Queued per character so a burst of edits to ONE crawler lands in order.
    return this.queue.run(`character:${stamped.id}`, async () => {
      await this.storage.writeCharacter(stamped.id, stamped);
      await this.mutateIndex((index) => upsertEntry(index, entryFor(stamped)));
      return stamped;
    });
  }

  async delete(id: string): Promise<void> {
    await this.queue.run(`character:${id}`, async () => {
      await this.storage.deleteCharacter(id);
      await this.mutateIndex((index) => removeEntry(index, id));
    });
  }

  /**
   * Read-modify-write of the shared library index, under a single global lock.
   *
   * Without this, two different characters saving at the same time would each
   * read the index, each add only their own row, and the slower write would
   * overwrite the faster one, losing a crawler from the list. The per-character
   * queue cannot prevent that, because the two saves use different keys.
   */
  private mutateIndex(change: (index: LibraryIndex) => LibraryIndex): Promise<void> {
    return this.queue.run(INDEX_QUEUE_KEY, async () => {
      const index = await this.loadIndex();
      await this.storage.writeIndex(change(index));
    });
  }

  async duplicate(id: string, newName: string, newId: string): Promise<Character> {
    const original = await this.load(id);
    const copy: Character = {
      ...original,
      id: newId,
      name: newName,
      skills: original.skills.map((s) => ({ ...s })),
      unenhancedStats: { ...original.unenhancedStats },
      createdAt: this.now(),
      updatedAt: this.now(),
    };
    return this.save(copy);
  }

  describeLocation(): Promise<string> {
    return this.storage.describeLocation();
  }
}
